import { Link } from "react-router-dom";
import {
  BookOpen,
  FileText,
  Search,
  Newspaper,
  ArrowRight,
  AlertTriangle,
  Calculator,
  Clock,
  Scale,
  Sparkles,
  ShieldCheck,
  Phone,
} from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { CTASection } from "@/components/site/CTASection";
import { ResourceHero } from "@/components/site/ResourceHero";
import { irsNotices } from "@/data/irsNotices";
import { irsForms } from "@/data/site/irsForms";
import { glossaryTerms } from "@/data/site/glossary";
import { stockImages } from "@/data/site/stockImages";
import { brand } from "@/data/site/brand";
import { LeadMagnetCTA } from "@/components/site/LeadMagnetCTA";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";

type ResourceCard = {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  blurb: string;
  href: string;
};

const lookups: ResourceCard[] = [
  {
    icon: AlertTriangle,
    title: "IRS Notice Library",
    blurb: "Every common IRS notice explained — deadlines, consequences, exact next step.",
    href: "/resources/irs-notices",
  },
  {
    icon: FileText,
    title: "IRS Form Library",
    blurb: "The forms used in tax resolution — what they do and how they're used against you (or for you).",
    href: "/resources/irs-forms",
  },
  {
    icon: Search,
    title: "Notice Lookup Tool",
    blurb: "Type your notice number — CP14, LT11, CP2000 — and get the deadline and your next move.",
    href: "/resources/notice-lookup",
  },
];

const guides: ResourceCard[] = [
  {
    icon: Calculator,
    title: "How Much Does Tax Resolution Cost?",
    blurb: "Honest fee ranges for every type of IRS case, plus the red flags that mean you're being overcharged.",
    href: "/resources/how-much-does-tax-resolution-cost",
  },
  {
    icon: Clock,
    title: "How Long Does Tax Resolution Take?",
    blurb: "Realistic IRS timelines — installment agreements, OICs, audits, levy releases — and what speeds them up.",
    href: "/resources/how-long-does-tax-resolution-take",
  },
  {
    icon: Scale,
    title: "CPA vs. Tax Attorney for IRS Help",
    blurb: "Decision guide — who you actually need for collections, audits, OICs, and criminal cases.",
    href: "/resources/cpa-vs-tax-attorney-for-irs-help",
  },
];

const reference: ResourceCard[] = [
  {
    icon: BookOpen,
    title: "Tax Resolution Glossary",
    blurb: "Plain-English definitions of the acronyms, form numbers, and IRS jargon you'll hear during your case.",
    href: "/resources/glossary",
  },
  {
    icon: Newspaper,
    title: "Blog & Case Studies",
    blurb: "Strategy walkthroughs and updates from our CPA-led resolution team.",
    href: "/blog",
  },
];

const ResourceCardLink = ({ r }: { r: ResourceCard }) => (
  <Link
    to={r.href}
    className="group flex h-full flex-col rounded-lg border border-border bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-gold hover:shadow-md"
  >
    <span className="mb-4 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-navy transition-colors group-hover:bg-gold group-hover:text-navy-dark">
      <r.icon className="h-5 w-5" strokeWidth={1.75} />
    </span>
    <h3 className="font-heading text-lg font-bold leading-snug text-navy">{r.title}</h3>
    <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground/75">{r.blurb}</p>
    <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-navy group-hover:text-navy-dark">
      Open <ArrowRight className="h-3.5 w-3.5" />
    </span>
  </Link>
);

const Section = ({
  eyebrow,
  title,
  items,
}: {
  eyebrow: string;
  title: string;
  items: ResourceCard[];
}) => (
  <Reveal>
    <div className="mb-6 flex items-baseline justify-between gap-4">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold">{eyebrow}</p>
        <h2 className="mt-1 font-heading text-2xl lg:text-3xl font-bold text-navy leading-tight">
          {title}
        </h2>
      </div>
    </div>
    <RevealStagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" stagger={60}>
      {items.map((r) => (
        <RevealItem key={r.href}>
          <ResourceCardLink r={r} />
        </RevealItem>
      ))}
    </RevealStagger>
  </Reveal>
);

const ResourcesIndex = () => (
  <SiteLayout>
    <PageSEO
      title="Tax Resolution Resources"
      description="IRS notice and form libraries, a tax resolution glossary, the notice lookup tool, fee and timeline guides, and the CTR blog."
      path="/resources"
      image={stockImages.accountingDesk.src}
    />
    <ResourceHero
      breadcrumbs={[{ name: "Home", href: "/" }, { name: "Resources" }]}
      eyebrow="Free • No login"
      title="Tax resolution resources"
      summary="Look up the IRS notice you received, decode the jargon, see honest fee and timeline ranges, and read our blog — built for taxpayers under collection pressure."
      facts={[
        { label: "Notices", value: irsNotices.length },
        { label: "Forms", value: irsForms.length },
        { label: "Glossary terms", value: glossaryTerms.length },
      ]}
      background="accountingDesk"
    />

    {/* Start Here highlight */}
    <section className="py-12 lg:py-16 bg-cream">
      <div className="container mx-auto px-4 max-w-6xl">
        <Reveal>
          <div className="grid gap-6 rounded-xl bg-gradient-navy p-8 text-white shadow-xl lg:grid-cols-[1.2fr_1fr] lg:p-12">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
                <Sparkles className="h-3 w-3" /> Start here
              </span>
              <h2 className="mt-4 font-heading text-2xl lg:text-4xl font-bold leading-tight [text-wrap:balance]">
                Got a letter from the IRS? Look it up in 10 seconds.
              </h2>
              <p className="mt-3 max-w-xl text-white/75 leading-relaxed">
                Find the notice number in the top-right of your letter (e.g. <strong className="text-gold">CP14</strong>,{" "}
                <strong className="text-gold">LT11</strong>, <strong className="text-gold">CP2000</strong>). Type it in
                — we'll tell you the deadline, the consequences if you ignore it, and your exact next move.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/resources/notice-lookup"
                  className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-bold uppercase tracking-wider text-navy-dark transition-colors hover:bg-gold-light"
                >
                  <Search className="h-4 w-4" /> Open Notice Lookup
                </Link>
                <a
                  href={`tel:${brand.phoneTel}`}
                  className="inline-flex items-center gap-2 rounded-md border border-white/30 px-5 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-white/10"
                >
                  <Phone className="h-4 w-4 text-gold" /> {brand.phone}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-white/15 bg-white/5 p-5 text-sm">
              <ShieldCheck className="h-8 w-8 shrink-0 text-gold" />
              <p className="text-white/80 leading-snug">
                Every article on this site is written and reviewed by{" "}
                <strong className="text-white">CPAs with {brand.yearsExperience}+ years</strong> of IRS controversy
                experience. No SEO filler, no "tax relief" sales pitch.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Grouped resource sections */}
    <section className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 max-w-6xl space-y-16">
        <Section eyebrow="Look it up" title="Reference libraries & tools" items={lookups} />
        <Section eyebrow="Read first" title="Honest guides before you hire anyone" items={guides} />
        <Section eyebrow="Go deeper" title="Glossary & ongoing writing" items={reference} />
      </div>
    </section>

    <LeadMagnetCTA />

    <CTASection />
  </SiteLayout>
);

export default ResourcesIndex;
